import { Modal, ModalProps } from '@/modules/common/components/Modal';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Link, TextField, Typography } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { useUserPasswordResetRequest } from '../hooks/useAuth';

export type ConfirmResetPasswordDialogForValues = {
  email: string;
};

const FORM_SCHEMA = yup.object().shape({
  email: yup
    .string()
    .email('Please provide an valid e-mail address')
    .required('Please input your email address'),
});

type ConfirmResetPasswordDialog = Omit<
  ModalProps,
  'children' | 'title' | 'subTitle'
> & {
  defaultEmail?: string;
  onSuccess: () => void;
};

export function ConfirmResetPasswordDialog({
  onSuccess,
  defaultEmail,
  ...props
}: ConfirmResetPasswordDialog) {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ConfirmResetPasswordDialogForValues>({
    resolver: yupResolver(FORM_SCHEMA),
    defaultValues: {
      email: defaultEmail ?? '',
    },
  });

  const { mutateAsync: requestUserPasswordReset, isPending } =
    useUserPasswordResetRequest();

  const handlePasswordResetRequest = async ({
    email,
  }: ConfirmResetPasswordDialogForValues) => {
    try {
      await requestUserPasswordReset({
        email,
        redirectTo: `/login`,
      });
      onSuccess();
      enqueueSnackbar('Instructions were sent to your e-mail', {
        variant: 'success',
      });
      reset();
    } catch {
      enqueueSnackbar('An error occurred. Please try again later.', {
        variant: 'error',
      });
    }
  };

  return (
    <Modal
      {...props}
      title="Forgot Password?"
      onClose={(...args) => {
        reset();
        props.onClose?.(...args);
      }}
      classes={{
        paper: 'max-w-md',
      }}
      subTitle="Enter the email address you signed up with below. An email will be sent containing a link to reset your password."
    >
      <form
        onSubmit={handleSubmit(handlePasswordResetRequest)}
        className="flex flex-col justify-end gap-3"
      >
        <Typography>
          Forgot which email address you signed up with?{' '}
          <Link
            className="underline"
            href="mailto:support@mydra.io"
            target="_blank"
          >
            Get help.
          </Link>
        </Typography>
        <Controller
          control={control}
          name="email"
          render={({ field }) => (
            <TextField
              {...field}
              className="mt-9"
              placeholder="Email address"
              hiddenLabel
              aria-label="Email address"
            />
          )}
        />
        {errors.email?.message && (
          <p className="text-sm text-red-500">{errors.email.message}</p>
        )}
        <Button
          type="submit"
          className="w-full mt-6"
          variant="contained"
          loading={isPending}
        >
          Send reset instructions
        </Button>
      </form>
    </Modal>
  );
}
